import { Fragment, useEffect, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import logo from "../Assets/Branding/LogoWithTextWhite.png";
import eyeLogo from "../Assets/Branding/eye_logo.png";
import UserSettings from "../Pages/Private/Settings/UserSettings";
import SendFeedbackModal from "./SendFeedbackModal";
import TeamSwitcher from "./TeamSwitcher";
import { logout } from "../Requests/User/UserRequests";
import { useTranslation } from "react-i18next";
import { getCredits, getTeamUuid, isBetaUser } from "../utils/cookies";
import { useMyContext } from "./ContextProviders/CreditsContextProvider";
import { useTeamsContext } from "./ContextProviders/TeamsContextProvider";
import i18n from "../i18n";
import Flag from "react-flagkit";
import { useNavigate } from "react-router-dom";
import { BookOpenIcon, CalendarIcon, ChatBubbleBottomCenterIcon, ClipboardIcon, Cog8ToothIcon, CpuChipIcon, DocumentCheckIcon, GiftIcon, HomeIcon, UserGroupIcon, CreditCardIcon, BeakerIcon, ChevronDownIcon, ChevronRightIcon, UserIcon, PlusIcon, UsersIcon } from "@heroicons/react/20/solid";
import { useSidebar } from "./ContextProviders/SidebarContextProvider";
import CreateEditProjectSlideover from "./Projects/CreateEditProjectSlideover";


function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
function SideBar({ current_tab }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewFeedback, setViewFeedback] = useState(null);
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const collapseTimeoutRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [projectSlideover, setProjectSlideover] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { credits } = useMyContext();
  const { clearTeamData } = useTeamsContext();

  const showFeedback = () => {
    setViewFeedback(<SendFeedbackModal key={Math.random()} />)
  }

  const showProjectCreation = () => {
    setProjectSlideover(<CreateEditProjectSlideover key={Math.random()} handleClose={() => setProjectSlideover(null)} />)
  }

  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang)
  }

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  const isItemExpanded = (itemName) => {
    return expandedItems.has(itemName);
  };

  const renderNavigationItem = (item, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const expanded = isItemExpanded(item.name);
    
    const handleClick = () => {
      if (item.onClick) {
        item.onClick();
      } else if (hasChildren && !item.href) {
        toggleExpanded(item.name);
      } else if (item.name === t('Sidebar.Feedback')) {
        showFeedback();
      } else if (item.href) {
        window.location.href = item.href;
      }
    };

    return (
      <div key={item.name}>
        <a
          onClick={handleClick}
          href={item.href || "#"}
          target={item.target}
          className={classNames(
            item.current
              ? "bg-iavisus-900 text-white"
              : "text-white hover:bg-iavisus-300 hover:text-white",
            "group flex items-center text-sm font-medium rounded-md px-2 py-2 cursor-pointer",
            isChild ? "pl-8" : ""
          )}
        >
          <item.icon
            className={classNames(
              item.current
                ? "text-white group-hover:text-white"
                : "text-white group-hover:text-white",
              "flex-shrink-0 h-6 w-6", !isCollapsed || isChild ? "mr-3" : ""
            )}
            aria-hidden="true"
          />
          {(!isCollapsed || isChild) && <span className="flex-1">{item.name}</span>}
          {hasChildren && !isCollapsed && (
            <div className="ml-auto">
              {expanded ? (
                <ChevronDownIcon className="h-4 w-4 text-white" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-white" />
              )}
            </div>
          )}
        </a>
        
        {hasChildren && expanded && !isCollapsed && (
          <div className="mt-1 space-y-1 overflow-hidden transition-all duration-200 ease-in-out">
            {item.children.map((child) => renderNavigationItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  const renderMobileNavigationItem = (item, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const expanded = isItemExpanded(item.name);
    
    const handleClick = () => {
      if (item.onClick) {
        item.onClick();
      } else if (hasChildren && !item.href) {
        toggleExpanded(item.name);
      } else if (item.name === t('Sidebar.Feedback')) {
        showFeedback();
      } else if (item.href) {
        window.location.href = item.href;
      }
    };

    return (
      <div key={item.name}>
        <a
          onClick={handleClick}
          href={item.href || "#"}
          target={item.target}
          className={classNames(
            item.current
              ? "bg-iavisus-900 text-white"
              : "text-white hover:bg-iavisus-300 hover:text-white",
            "group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer",
            isChild ? "pl-8" : ""
          )}
        >
          <item.icon
            className={classNames(
              item.current
                ? "text-white group-hover:text-white"
                : "text-white group-hover:text-white",
              "mr-3 flex-shrink-0 h-6 w-6"
            )}
            aria-hidden="true"
          />
          <span className="flex-1">{item.name}</span>
          {hasChildren && (
            <div className="ml-auto">
              {expanded ? (
                <ChevronDownIcon className="h-4 w-4 text-white" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-white" />
              )}
            </div>
          )}
        </a>
        
        {hasChildren && expanded && (
          <div className="mt-1 space-y-1 overflow-hidden transition-all duration-200 ease-in-out">
            {item.children.map((child) => renderMobileNavigationItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  // Handle mouse enter - clear any pending collapse timeout
  const handleMouseEnter = () => {
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }
    setIsCollapsed(false);
  };

  // Handle mouse leave - set timeout to collapse after half a second
  const handleMouseLeave = () => {
    collapseTimeoutRef.current = setTimeout(() => {
      setIsCollapsed(true);
      collapseTimeoutRef.current = null;
    }, 500);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);

  // Auto-expand Settings if any of its children are current
  useEffect(() => {
    if (current_tab == "Settings" || current_tab == "Rewards" || current_tab == "Credits" || current_tab == "Billing" || current_tab == "Team") {
      setExpandedItems(prev => new Set(prev).add(t('Sidebar.SettingsNavTitle')));
    }
    // Auto-expand Projects if current
    if (current_tab == "Projects") {
      setExpandedItems(prev => new Set(prev).add(t('Sidebar.Projects')));
    }
  }, [current_tab, t]);

  const navigation = [
    { name: t('Sidebar.StartNavTitle'), href: "/dashboard", icon: HomeIcon, current: (current_tab == "Dashboard") },
    { 
      name: t('Sidebar.Projects'), 
      href: null, 
      icon: ClipboardIcon, 
      current: (current_tab == "Projects"),
      children: [
        { name: t('Projects.Title'), href: "/projects", icon: ClipboardIcon, current: (current_tab == "Projects") },
        { name: t('Projects.ActionTitle'), href: null, icon: PlusIcon, current: false, onClick: showProjectCreation },
      ]
    },
    { name: t('Sidebar.Participants'), href: "/participants", icon: UsersIcon, current: (current_tab == "Participants") },
    // Only show AI Researcher for beta users
    ...(isBetaUser() ? [{ name: t('Sidebar.AIResearcher'), href: "/research-pipeline", icon: BeakerIcon, current: (current_tab == "AI Researcher") }] : []),
    { 
      name: t('Sidebar.SettingsNavTitle'), 
      href: null, 
      icon: Cog8ToothIcon, 
      current: (current_tab == "Settings" || current_tab == "Rewards" || current_tab == "Credits" || current_tab == "Billing" || current_tab == "Team"),
      children: [
        { name: "Definições de conta", href: "/account_settings", icon: UserIcon, current: (current_tab == "Settings") },
        { name: t('Sidebar.TeamNavTitle'), href: "/team", icon: UserGroupIcon, current: (current_tab == "Team") },
        { name: t('Sidebar.Rewards'), href: "/rewards", icon: GiftIcon, current: (current_tab == "Rewards") },
        { name: t('Sidebar.CreditsNavTitle'), href: "/pricing/credits", icon: CpuChipIcon, current: (current_tab == "Credits") },
        { name: t('Sidebar.Billing'), href: "/billing", icon: CreditCardIcon, current: (current_tab == "Billing") },
      ]
    },
    { name: t('Sidebar.Guides'), href: t('Sidebar.GuidesUrl'), icon: BookOpenIcon, current: (current_tab == "Guides"), target: "_blank" },
    { name: t('Sidebar.Feedback'), href: "/", icon: ChatBubbleBottomCenterIcon, current: (current_tab == "") },
  ];



  const deprecated = [
    //{ name: t('Sidebar.MeetingsNavTitle'), href: "/meetings", icon: CalendarIcon, current: (current_tab == "Meetings") },
    //{ name: t('Sidebar.JoinAnalysis'), href: "/analysis", icon: DocumentCheckIcon, current: (current_tab == "Analysis") },
  ]

  return (
    <div className="print:hidden">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gradient-to-b from-iavisus-100 via-iavisus-100 to-black" style={{ willChange: 'transform', backfaceVisibility: 'hidden', transform: 'translateZ(0)' }}>
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">{t('Sidebar.CloseSidebar')}</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                  <div className="flex flex-shrink-0 items-center px-4">
                    <div className="w-full flex justify-center">
                      <img
                        className="h-24 w-auto"
                        src={logo}
                        alt="IAVISUS LABS"
                      />
                    </div>
                  </div>
                  <TeamSwitcher />
                  <nav className="mt-5 space-y-1 px-2">
                    {navigation.map((item) => renderMobileNavigationItem(item))}

                    {/* <div className="text-sm text-gray-400 ml-2 pt-4">Descontinuados</div> */}
                    {deprecated.map(function (item) {
                      return item.name == t('Sidebar.Feedback') ?
                        (<a
                          key={item.name}
                          onClick={showFeedback}
                          className={classNames(
                            item.current
                              ? "bg-iavisus-800 text-white"
                              : "text-gray-300 hover:bg-iavisus-300 hover:text-white",
                            "group flex items-center px-2 py-2 text-xs font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-400 group-hover:text-white"
                                : "text-gray-400 group-hover:text-white",
                              "flex-shrink-0 h-6 w-6", !isCollapsed ? "mr-3" : ""
                            )}
                            aria-hidden="true"
                          />
                          {!isCollapsed && <span>{item.name}</span>}
                        </a>)
                        :
                        (<a
                          key={item.name}
                          href={item.href}
                          className={classNames(
                            item.current
                              ? "bg-iavisus-800 text-white"
                              : "text-gray-300 hover:bg-iavisus-300 hover:text-white",
                            "group flex items-center px-2 py-2 text-xs font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-200 "
                                : "text-gray-400 group-hover:text-white",
                              "flex-shrink-0 h-6 w-6", !isCollapsed ? "mr-3" : ""
                            )}
                            aria-hidden="true"
                          />
                          {!isCollapsed && <span>{item.name}</span>}
                        </a>)
                    })}
                  </nav>

                </div>

                <div className="flex flex-col">
                  <div className="w-full flex justify-start gap-2 px-6">
                    <button onClick={() => handleChangeLanguage("pt")}><Flag country="PT" className="rounded-full w-7" /></button>
                    <button onClick={() => handleChangeLanguage("en")} ><Flag country="GB" className="rounded-full w-7" /></button>
                    <button onClick={() => handleChangeLanguage("es")} ><Flag country="ES" className="rounded-full w-7" /></button>
                  </div>
                  <button className="mx-4 my-4  text-sm flex items-center justify-between border-2 rounded-full shadow-xl"
                    onClick={() => {
                      navigate("/pricing/credits")
                    }}
                  >
                    <div className="flex items-center mx-4 gap-2 text-white">
                      <CpuChipIcon className="w-6 h-6 text-white"></CpuChipIcon>
                      {credits?.toFixed(2) || 0}
                    </div>
                    <div className=" bg-iavisus-900 rounded-full font-bold text-sm text-white px-4 py-2 h-full hover:bg-white hover:text-iavisus-900 border border-iavisus-900">
                      {t('Sidebar.CreditsLabel')}
                    </div>
                  </button>
                  <div className="flex flex-shrink-0 bg-black  p-4">
                    <a
                      onClick={() => {
                        logout().then((res) => {
                          clearTeamData();
                          navigate("/login")
                        }).catch((e) => {
                          clearTeamData();
                          navigate("/login")
                        })
                      }}
                      href="/login"
                      className="group block w-full flex-shrink-0"
                    >
                      <div className="flex items-center">
                        <div></div>
                        <div className="ml-3">
                          <p className="text-xs font-medium text-white group-hover:text-white">
                            {t('Sidebar.LogoutText')}
                          </p>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <div
        className={`hidden md:fixed md:inset-y-0 md:flex md:flex-col transition-all duration-300 ${isCollapsed ? 'md:w-16' : 'md:w-64'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex min-h-0 flex-1 flex-col bg-gradient-to-b from-iavisus-100 via-iavisus-100 to-black">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center h-24">
              <div className="w-full flex justify-center items-center h-full">
                {!isCollapsed ? (
                  <img className="h-20 w-auto" src={logo} alt="IAVISUS LABS" />
                ) : (
                  <img className="h-8 w-8 rounded-lg" src={eyeLogo} alt="Eye Logo" />
                )}
              </div>
            </div>
            <div className="h-16">
              {!isCollapsed && <TeamSwitcher />}
            </div>
            <nav className="mt-5 flex-1 space-y-1 px-2">
              {navigation.map((item) => renderNavigationItem(item))}
              {/*<div className="text-sm text-gray-400 ml-2 pt-4">{t('Sidebar.Deprecated')}</div>*/}
              {deprecated.map(function (item) {
                return item.name == "Feedback" ?
                  (<a
                    key={item.name}
                    onClick={showFeedback}
                    className={classNames(
                      item.current
                        ? "bg-iavisus-800 text-white"
                        : "text-gray-300 hover:bg-iavisus-300 hover:text-white",
                      "group flex items-center px-2 py-2 text-xs font-medium rounded-md"
                    )}
                  >
                    <item.icon
                      className={classNames(
                        item.current
                          ? "text-gray-400 group-hover:text-white"
                          : "text-gray-400 group-hover:text-white",
                        "flex-shrink-0 h-6 w-6", !isCollapsed ? "mr-3" : ""
                      )}
                      aria-hidden="true"
                    />
                    {!isCollapsed && <span>{item.name}</span>}
                  </a>)
                  :
                  (<a
                    key={item.name}
                    href={item.href}
                    className={classNames(
                      item.current
                        ? "bg-iavisus-800 text-white"
                        : "text-gray-300 hover:bg-iavisus-300 hover:text-white",
                      "group flex items-center px-2 py-2 text-xs font-medium rounded-md"
                    )}
                  >
                    <item.icon
                      className={classNames(
                        item.current
                          ? "text-gray-200 "
                          : "text-gray-400 group-hover:text-white",
                        "flex-shrink-0 h-6 w-6", !isCollapsed ? "mr-3" : ""
                      )}
                      aria-hidden="true"
                    />
                    {!isCollapsed && <span>{item.name}</span>}
                  </a>)
              })}
            </nav>
          </div>
          <div className="ml-4"></div>
          {!isCollapsed && (
            <div className="w-full flex justify-start gap-2 px-6">
              <button onClick={() => handleChangeLanguage("pt")}><Flag country="PT" className="rounded-full w-7" /></button>
              <button onClick={() => handleChangeLanguage("en")} ><Flag country="GB" className="rounded-full w-7" /></button>
              <button onClick={() => handleChangeLanguage("es")} ><Flag country="ES" className="rounded-full w-7" /></button>
            </div>
          )}
          {!isCollapsed ? (
            <button className="mx-4 my-4  text-sm flex items-center justify-between border-2 rounded-full shadow-xl"
              onClick={() => {
                navigate("/pricing/credits")
              }}
            >
              <div className="flex items-center mx-4 gap-2 text-white">
                <CpuChipIcon className="w-6 h-6 text-white"></CpuChipIcon>
                {credits?.toFixed(2) || 0}
              </div>
              <div className=" bg-iavisus-900 rounded-full font-bold text-sm text-white px-4 py-2 h-full hover:bg-white hover:text-iavisus-900 border border-iavisus-900">
                {t('Sidebar.CreditsLabel')}
              </div>
            </button>
          ) : (
            <button className="mx-4 my-4 p-2 flex items-center justify-center border-2 rounded-full shadow-xl text-white"
              onClick={() => {
                navigate("/pricing/credits")
              }}
            >
              <CpuChipIcon className="w-6 h-6 text-white"></CpuChipIcon>
            </button>
          )}
          <div className="flex flex-shrink-0 bg-black  p-4">
            <a
              onClick={() => {
                logout().then(() => {
                  clearTeamData();
                  navigate("/login");
                }).catch(() => {
                  clearTeamData();
                  navigate("/login");
                });
              }}
              href="/login"
              className={`group block w-full flex-shrink-0 ${isCollapsed ? 'flex justify-center' : ''}`}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                {!isCollapsed && (
                  <>
                    <div></div>
                    <div className="ml-3">
                      <p className="text-xs font-medium text-white group-hover:text-white">
                        {t('Sidebar.LogoutText')}
                      </p>
                    </div>
                  </>
                )}
                {isCollapsed && (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                  </svg>
                )}
              </div>
            </a>
          </div>
        </div>
      </div>
      <div className="sticky top-0 z-10 bg-gradient-to-b from-iavisus-100 via-iavisus-100 to-black pl-1 pt-1 sm:pl-3 sm:pt-3 md:hidden">

        <button
          type="button"
          className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">{t('Sidebar.OpenSidebar')}</span>
          <Bars3Icon className="h-6 w-6 text-white" aria-hidden="true" />
        </button>
      </div>
      {viewFeedback}
      {projectSlideover}
    </div>
  );
}

export default SideBar;
